/**
 * Integrations gallery — S6 of FEATURE_ROADMAP §19 Phase 1.
 *
 * One page, card-based layout, six integration categories. Today only
 * Identity (SSO) and Compliance (existing GRC layer) are clickable;
 * Notifications / Issue Tracking / SIEM / Source Control are visible
 * but locked with "Available on Enterprise" or "Coming soon" chips so
 * customers can see the roadmap.
 *
 * Tier gating mirrors the CustomRulesPage pattern: tenant config has
 * tier + max_custom_rules already; we use tier directly for SSO since
 * SSO is Enterprise-default with Business as an add-on.
 */
import { useEffect, useState } from 'react'
import {
  Box, Card, CardContent, Typography, Button, Chip, Grid, Tooltip,
  CircularProgress, Alert, Link as MuiLink,
} from '@mui/material'
import {
  VpnKey, Notifications, BugReport, Gavel, Shield, AccountTree, Lock, OpenInNew,
} from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { tenantApi } from '../services/api'
import { RootState } from '../store'
import SsoConfigDialog from '../components/SsoConfigDialog'
import UserAccessPanel from '../components/UserAccessPanel'

interface IntegrationCard {
  id: string
  category: string
  name: string
  description: string
  icon: JSX.Element
  status: 'available' | 'configured' | 'tier_locked' | 'coming_soon'
  // For 'available' / 'configured':
  onConfigure?: () => void
  // For 'tier_locked':
  requiredTier?: string
  // For 'configured' (extra metadata):
  statusDetail?: string
}

const TIER_TO_LEVEL: Record<string, number> = {
  trial: 0, free: 0, team: 1, business: 2, enterprise: 3, design_partner: 3,
}

const tierAllows = (tier: string, minLevel: number) =>
  (TIER_TO_LEVEL[tier?.toLowerCase()] ?? 0) >= minLevel

const IntegrationsPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id

  const [tier, setTier] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [ssoConfigured, setSsoConfigured] = useState(false)
  const [ssoEnabled, setSsoEnabled] = useState(false)
  const [ssoDialogOpen, setSsoDialogOpen] = useState(false)

  useEffect(() => {
    if (!tenantId) { setLoading(false); return }
    let cancelled = false
    const load = async () => {
      try {
        const t = await tenantApi.getTenant(tenantId)
        if (cancelled) return
        setTier(t.data?.tier || '')
      } catch {}
      try {
        const s = await tenantApi.getSsoConfig(tenantId)
        if (cancelled) return
        setSsoConfigured(true)
        setSsoEnabled(!!s.data?.enabled)
      } catch (e: any) {
        if (e.response?.status === 404) {
          setSsoConfigured(false); setSsoEnabled(false)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [tenantId])

  const refreshSso = async () => {
    if (!tenantId) return
    try {
      const s = await tenantApi.getSsoConfig(tenantId)
      setSsoConfigured(true); setSsoEnabled(!!s.data?.enabled)
    } catch (e: any) {
      if (e.response?.status === 404) { setSsoConfigured(false); setSsoEnabled(false) }
    }
  }

  // SSO is Enterprise-default, available as Business add-on per §19 plan
  const ssoTierOk = tierAllows(tier, TIER_TO_LEVEL.business)
  const ssoStatus: IntegrationCard['status'] =
    !ssoTierOk ? 'tier_locked'
    : ssoEnabled ? 'configured'
    : ssoConfigured ? 'available'  // configured but not enabled — show as available with detail
    : 'available'

  const ssoStatusDetail = ssoEnabled
    ? 'Active for all users in this tenant'
    : ssoConfigured ? 'Configured but not yet enabled'
    : undefined

  const cards: IntegrationCard[] = [
    // Identity — the only fully-built one in this release
    {
      id: 'sso_oidc',
      category: 'Identity',
      name: 'Single Sign-On (OIDC)',
      description: 'Connect Okta, Azure AD / Entra, Google Workspace, Auth0, Keycloak, or any OIDC-compliant IdP. JIT user provisioning + group-based role mapping.',
      icon: <VpnKey />,
      status: ssoStatus,
      requiredTier: 'Business or Enterprise',
      statusDetail: ssoStatusDetail,
      onConfigure: ssoTierOk ? () => setSsoDialogOpen(true) : undefined,
    },
    // GRC — links out to the existing Compliance Posture integration surface
    {
      id: 'grc_universal',
      category: 'Compliance & GRC',
      name: 'GRC Universal Integration',
      description: 'OSCAL-format exports + evidence webhooks + scoped API keys. Works with ServiceNow IRM, Drata, Vanta, AuditBoard, and any GRC tool that consumes OSCAL.',
      icon: <Gavel />,
      status: 'available',
      onConfigure: undefined,  // No dialog — link to /compliance
    },

    // Roadmap placeholders — visible so customers know what's coming
    {
      id: 'slack',
      category: 'Notifications',
      name: 'Slack',
      description: 'Channel webhooks for finding alerts, drift detection, scan completion.',
      icon: <Notifications />, status: 'coming_soon',
    },
    {
      id: 'teams',
      category: 'Notifications',
      name: 'Microsoft Teams',
      description: 'Channel notifications for Microsoft-shop customers.',
      icon: <Notifications />, status: 'coming_soon',
    },
    {
      id: 'pagerduty',
      category: 'Notifications',
      name: 'PagerDuty',
      description: 'Page oncall for critical drift or autonomous-agent failures.',
      icon: <Notifications />, status: 'coming_soon',
    },
    {
      id: 'jira',
      category: 'Issue Tracking',
      name: 'Jira',
      description: 'Convert findings into tracked tickets with close-the-loop status sync.',
      icon: <BugReport />, status: 'coming_soon',
    },
    {
      id: 'servicenow_itsm',
      category: 'Issue Tracking',
      name: 'ServiceNow ITSM',
      description: 'Findings as incidents/problems in your existing ServiceNow instance.',
      icon: <BugReport />, status: 'coming_soon',
    },
    {
      id: 'splunk',
      category: 'Security & SIEM',
      name: 'Splunk',
      description: 'Forward findings + audit logs to your security team\'s SIEM.',
      icon: <Shield />, status: 'coming_soon',
    },
    {
      id: 'datadog',
      category: 'Security & SIEM',
      name: 'Datadog',
      description: 'Stream ARIAS events into your existing observability platform.',
      icon: <Shield />, status: 'coming_soon',
    },
    {
      id: 'github_pr',
      category: 'Source Control',
      name: 'GitHub PR comments',
      // Said "CI gate already posts status checks", and ARIAS posts none. The
      // gate is a request your pipeline makes; the check your branch rules see
      // is your pipeline's job, under your pipeline's name. ARIAS holding a
      // check of its own needs an App identity it does not have.
      description: 'Inline finding comments on pull requests, and a status check posted by ARIAS rather than by your own pipeline step.',
      icon: <AccountTree />, status: 'coming_soon',
    },
    {
      id: 'gitlab',
      category: 'Source Control',
      name: 'GitLab',
      description: 'The same, for merge requests.',
      icon: <AccountTree />, status: 'coming_soon',
    },
  ]

  const categories = Array.from(new Set(cards.map(c => c.category)))

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Integrations</Typography>
        <Typography variant="body2" color="text.secondary">
          Connect ARIAS to your identity provider, notification channels, GRC tools, ticketing systems, and SIEM.
        </Typography>
      </Box>

      {!ssoTierOk && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your current plan ({tier || 'Trial'}) doesn't include all integrations. Identity (SSO) and other enterprise integrations are available on Business and Enterprise plans.{' '}
          <MuiLink component={RouterLink} to="/pricing">View pricing</MuiLink>.
        </Alert>
      )}

      {categories.map(cat => (
        <Box key={cat} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {cat}
          </Typography>
          <Grid container spacing={2}>
            {cards.filter(c => c.category === cat).map(card => (
              <Grid item xs={12} sm={6} md={4} key={card.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Box sx={{ color: 'primary.main', mt: 0.25 }}>{card.icon}</Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{card.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {card.status === 'configured' && (
                            <Chip label="Active" size="small" color="success" />
                          )}
                          {card.status === 'available' && card.statusDetail && (
                            <Chip label="Configured" size="small" color="info" variant="outlined" />
                          )}
                          {card.status === 'tier_locked' && (
                            <Chip icon={<Lock sx={{ fontSize: 14 }} />} label={card.requiredTier || 'Enterprise'} size="small" variant="outlined" color="warning" />
                          )}
                          {card.status === 'coming_soon' && (
                            <Chip label="Coming soon" size="small" variant="outlined" />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                    {card.statusDetail && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                        {card.statusDetail}
                      </Typography>
                    )}
                  </CardContent>
                  <Box sx={{ p: 1.5, pt: 0 }}>
                    {card.id === 'grc_universal' ? (
                      <Button component={RouterLink} to="/compliance" size="small" endIcon={<OpenInNew />}>
                        Open Compliance Posture
                      </Button>
                    ) : card.status === 'configured' || card.status === 'available' ? (
                      <Button size="small" variant={card.status === 'configured' ? 'outlined' : 'contained'} onClick={card.onConfigure}>
                        {card.status === 'configured' ? 'Manage' : 'Configure'}
                      </Button>
                    ) : card.status === 'tier_locked' ? (
                      <Tooltip title={`Available on ${card.requiredTier || 'Enterprise'} plan`}>
                        <span>
                          <Button size="small" disabled>Configure</Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button size="small" disabled>Notify me</Button>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* User Access — Phase 1.5 (visibility + per-user disable + break-glass).
          Shown to tenants on Business+ tier (mirror SSO gating) so they have
          a place to manage the password→SSO transition. */}
      {ssoTierOk && tenantId && (
        <Box sx={{ mt: 4 }}>
          <UserAccessPanel tenantId={tenantId} ssoEnabled={ssoEnabled} />
        </Box>
      )}

      <SsoConfigDialog
        open={ssoDialogOpen}
        onClose={() => { setSsoDialogOpen(false); refreshSso() }}
        tenantId={tenantId || ''}
        onSaved={refreshSso}
      />
    </Box>
  )
}

export default IntegrationsPage
